import React from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

/**
 * Sitemap-import dialog. Extracted from ChatbotDetailPage.jsx (iter132.2).
 * ``maxPages`` accepts '' during editing so we don't stomp on the user
 * while they type; parent normalizes on blur.
 */
export const SitemapImportDialog = ({
  open,
  onOpenChange,
  sitemapUrl,
  setSitemapUrl,
  maxPages,
  setMaxPages,
  deepCrawl,
  setDeepCrawl,
  importingSitemap,
  onSubmit,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-white border-slate-200 max-w-lg">
      <DialogHeader>
        <DialogTitle className="text-slate-900">Import from Sitemap</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label className="text-slate-600">Sitemap URL</Label>
          <Input
            value={sitemapUrl}
            onChange={(e) => setSitemapUrl(e.target.value)}
            placeholder="https://example.com/sitemap.xml"
            className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 mt-1"
            data-testid="sitemap-url-input"
          />
          <p className="text-xs text-slate-500 mt-1">
            Enter the URL of an XML sitemap to import multiple pages at once
          </p>
        </div>
        <div>
          <Label className="text-slate-600">Maximum Pages</Label>
          <Input
            type="number"
            value={maxPages || ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                setMaxPages('');
              } else {
                const num = parseInt(val, 10);
                if (!isNaN(num)) {
                  setMaxPages(Math.min(50, Math.max(1, num)));
                }
              }
            }}
            onBlur={(e) => {
              // Restore default when the field is left blank or garbage
              if (e.target.value === '' || isNaN(parseInt(e.target.value, 10))) {
                setMaxPages(20);
              }
            }}
            min={1}
            max={50}
            placeholder="1-50"
            className="bg-white border-slate-300 text-slate-900 mt-1 w-32"
          />
          <p className="text-xs text-slate-500 mt-1">Limit: 1-50 pages per import</p>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <input
            type="checkbox"
            id="deep-crawl"
            checked={deepCrawl}
            onChange={(e) => setDeepCrawl(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 bg-white text-blue-600 focus:ring-blue-500"
          />
          <div>
            <Label htmlFor="deep-crawl" className="text-slate-700 cursor-pointer">Enable Deep Crawl</Label>
            <p className="text-xs text-slate-500">
              If sitemap is unavailable, automatically discover pages by following links from the homepage
            </p>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-700">
            <strong>Tip:</strong> Most websites have a sitemap at /sitemap.xml. The import will crawl each page and extract its content for your knowledge base.
          </p>
        </div>
      </div>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          className="border-slate-300 text-slate-700"
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={importingSitemap || !sitemapUrl.trim()}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="sitemap-import-submit"
        >
          {importingSitemap && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Import Pages
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default SitemapImportDialog;
